import type { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { PageContainer } from '../../nav/PageContainer';

// =============================================================================
// Shared back-office console primitives (Wave 3b).
//
// The original BackOffice page kept these styles inline; the new console pages
// (Plans/Usage/Platform/Notices/Reports/Impersonation) reuse them so the BO
// surface stays visually consistent and we avoid copy-pasting Tailwind strings.
// =============================================================================

export const inputCls =
  'rounded border border-line bg-panel-2 px-2.5 py-1.5 text-[15px] text-text outline-none transition-colors focus:border-accent-ring placeholder:text-text-faint';

export const btnPrimary =
  'inline-flex h-8 items-center gap-1.5 rounded border border-accent-ring bg-accent-soft px-3 text-[14px] font-semibold text-accent transition-colors hover:border-accent hover:bg-accent hover:text-bg disabled:cursor-not-allowed disabled:opacity-50';

export const btnGhost =
  'inline-flex h-7 items-center rounded border border-line px-2.5 text-[13px] text-text-dim transition-colors hover:border-line-strong hover:text-text disabled:opacity-40';

/** Standard page scaffold: scrollable body, centered column, title + subtitle. */
export function BoPage({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  /** @deprecated 설명 문구는 표시하지 않는다. 호출부 호환용. */
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <PageContainer variant="content" fluid inSection>
      {/* 제목·설명은 섹션 머리(운영사 탭)가 담당한다 — 2026-09-14 레이아웃 통일. */}
      {actions && <div className="flex items-center justify-end gap-2" aria-label={`${title} 액션`}>{actions}</div>}
      {children}
    </PageContainer>
  );
}

export function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="rounded border border-line bg-panel p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold tracking-wide text-text">{title}</h2>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function ErrorBanner({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p className="rounded bg-warning/10 px-3 py-2 text-[14px] text-warning" role="alert">
      {message}
    </p>
  );
}

export function OkBanner({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return <p className="rounded bg-green/10 px-3 py-2 text-[14px] text-green">{message}</p>;
}

export function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Compact human-readable byte size. limit=null renders as ∞. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const v = n / Math.pow(1024, i);
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** Format a limit value (bytes) for display; null = unlimited. */
export function formatBytesLimit(n: number | null): string {
  return n === null ? '∞' : formatBytes(n);
}

/** Format a count limit; null = unlimited. */
export function formatCountLimit(n: number | null): string {
  return n === null ? '∞' : String(n);
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ko-KR');
}
